import { SxProps, Theme } from '@mui/material/styles';
import { typographyClasses } from '@mui/material/Typography';
import { isNil } from 'lodash';
import { TypographyProps } from '@/components/atoms/Typography/types';
import palette from '@/theme/palette';

/**
 * Throws an error at runtime if sx contains fontSize, fontWeight, lineHeight, or color. Design system components
 * should not have key properties changed or color changed via sx properties.
 *
 * @param sx
 */
export const restrictedSxPropertiesError = (sx?: SxProps<Theme>) => {
  const hasFontSize = sx?.hasOwnProperty('fontSize');
  const hasFontWeight = sx?.hasOwnProperty('fontWeight');
  const hasLineHeight = sx?.hasOwnProperty('lineHeight');
  const hasColor = sx?.hasOwnProperty('color');
  const hasTextAlign = sx?.hasOwnProperty('textAlign');

  if (hasFontSize) {
    throw new Error('fontSize cannot be changed.');
  }

  if (hasFontWeight) {
    throw new Error('fontWeight cannot be changed.');
  }

  if (hasLineHeight) {
    throw new Error('lineHeight cannot be changed.');
  }

  if (hasColor) {
    throw new Error('color cannot be changed by sx properties. Use the color prop instead.');
  }

  if (hasTextAlign) {
    throw new Error('textAlign cannot be changed by sx properties. Use the align prop instead.');
  }
};

export const textOverflowSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // A flex item's default `min-width: auto` floors it at its content's intrinsic
  // width, which blocks shrinking below that — and ellipsis truncation needs the
  // element to shrink. Overriding it to 0 is what lets `maxWidth` (below) win.
  minWidth: 0,
  [`&.${typographyClasses.caption}, &.${typographyClasses.overline}`]: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
};

const TEXT_COLOR_TOKENS: Record<keyof typeof palette.light.text, string> = {
  primary: 'text.primary',
  secondary: 'text.secondary',
  disabled: 'text.disabled',
  info: 'text.info',
  success: 'text.success',
  warning: 'text.warning',
  error: 'text.error',
};

export const getPaletteColor = (color: TypographyProps['color']) => {
  if (isNil(color)) return TEXT_COLOR_TOKENS.primary;

  if (typeof color === 'function') {
    return color;
  }

  if (Object.prototype.hasOwnProperty.call(TEXT_COLOR_TOKENS, color)) {
    return TEXT_COLOR_TOKENS[color as keyof typeof TEXT_COLOR_TOKENS];
  }

  return color;
};
